/* RoleAnalytics — single component that powers the analytics tab for
 * CM / Admin / Finance / Audits / Sales. Picks endpoint by `role` prop and
 * renders only the sections relevant to that role.
 *
 * Endpoints: GET /api/role-analytics/{role}?period=&start_date=&end_date=
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Calendar, TrendingUp, IndianRupee, Coins, Clock, Users, Building2, MapPin,
  Loader2, AlertTriangle, Award, FileText, Ticket as TicketIcon, Star,
  CheckCircle2, XCircle, Hourglass, ArrowUpRight, ArrowDownRight, Banknote,
  Receipt, PieChart as PieChartIcon, Percent, ScrollText, FileWarning,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ['#c4663a', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#a78bfa'];

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');

const KpiCard = ({ icon: Icon, color = 'text-primary', label, value, sub, testid }) => (
  <Card className="bg-card" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-1 truncate">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
        <Icon className={`w-6 h-6 ${color} flex-shrink-0`} />
      </div>
    </CardContent>
  </Card>
);

const RangePicker = ({ period, setPeriod, custom, setCustom, onApply, loading }) => (
  <Card className="bg-card">
    <CardContent className="p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Label className="font-medium">Range:</Label>
        <div className="flex flex-wrap gap-2">
          {[
            { v: 'today', l: 'Today' },
            { v: 'week', l: 'This Week' },
            { v: 'month', l: 'This Month' },
            { v: 'quarter', l: 'Quarter' },
            { v: 'half_year', l: 'Half Year' },
            { v: 'year', l: 'YTD' },
            { v: 'custom', l: 'Custom' },
          ].map((p) => (
            <Button
              key={p.v}
              size="sm"
              variant={period === p.v ? 'default' : 'outline'}
              onClick={() => { setPeriod(p.v); if (p.v !== 'custom') onApply(p.v); }}
              data-testid={`range-${p.v}`}
            >
              {p.l}
            </Button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-auto">
            <Input
              type="date"
              value={custom.start}
              onChange={(e) => setCustom({ ...custom, start: e.target.value })}
              className="w-40"
              data-testid="custom-start-date"
            />
            <span className="text-sm">to</span>
            <Input
              type="date"
              value={custom.end}
              onChange={(e) => setCustom({ ...custom, end: e.target.value })}
              className="w-40"
              data-testid="custom-end-date"
            />
            <Button size="sm" onClick={() => onApply('custom')} disabled={!custom.start || !custom.end}>
              Apply
            </Button>
          </div>
        )}
        {loading && <Loader2 className="w-4 h-4 animate-spin text-primary ml-auto" />}
      </div>
    </CardContent>
  </Card>
);

/* ---------- Revenue split pie (shared) ---------- */
const RevenueByMethodPie = ({ data }) => {
  const arr = useMemo(() => Object.entries(data || {})
    .filter(([_, v]) => Number(v) > 0)
    .map(([k, v]) => ({ name: k.replace('_', ' ').toUpperCase(), value: Number(v) })), [data]);
  if (!arr.length) {
    return <div className="text-sm text-muted-foreground text-center py-12">No revenue in this period</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={arr} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${e.name}`}>
          {arr.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => fmtINR(v)} />
      </PieChart>
    </ResponsiveContainer>
  );
};

/* ---------- Daily trend line (shared) ---------- */
const DailyTrendChart = ({ data, lines = ['revenue'] }) => {
  if (!data || !data.length) {
    return <div className="text-sm text-muted-foreground text-center py-12">No data in this period</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#88888833" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={PIE_COLORS[i]} strokeWidth={2} dot={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

/* ============================================================
 * MAIN
 * ============================================================ */
export default function RoleAnalytics({ role = 'admin', venueId = null }) {
  const [period, setPeriod] = useState('month');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const params = { period: p };
      if (p === 'custom') {
        params.start_date = custom.start;
        params.end_date = custom.end;
      }
      if (venueId) params.venue_id = venueId;
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/role-analytics/${role}`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error('Analytics load failed:', e);
      setError(e?.response?.data?.detail || 'Failed to load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [role, custom.start, custom.end, venueId]);

  useEffect(() => { load(period); /* initial + venueId change */ }, [load, venueId]);

  if (!data && loading) {
    return (
      <div className="flex flex-col items-center gap-3 py-24" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading analytics…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`analytics-${role}`}>
      <RangePicker period={period} setPeriod={setPeriod} custom={custom} setCustom={setCustom} onApply={load} loading={loading} />
      {data?.date_range && (
        <p className="text-xs text-muted-foreground">
          Showing data from <b>{data.date_range.start}</b> to <b>{data.date_range.end}</b>
          {data.date_range.operating_days != null && ` · ${data.date_range.operating_days} operating days`}
        </p>
      )}

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
        </Card>
      )}

      {data && role === 'cm' && <CMSections data={data} />}
      {data && role === 'admin' && <AdminSections data={data} />}
      {data && role === 'finance' && <FinanceSections data={data} />}
      {data && role === 'audits' && <AuditsSections data={data} />}
      {data && role === 'sales' && <SalesSections data={data} />}
    </div>
  );
}

/* ============================================================
 * CM SECTIONS
 * ============================================================ */
function CMSections({ data }) {
  const o = data.overview || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard icon={Calendar} color="text-primary" label="Total Bookings" value={fmtNum(o.total_bookings)} testid="kpi-cm-bookings" />
        <KpiCard icon={IndianRupee} color="text-emerald-500" label="Revenue" value={fmtINR(o.total_revenue)} sub={`GST ${fmtINR(o.gst_collected)}`} testid="kpi-cm-revenue" />
        <KpiCard icon={Coins} color="text-amber-500" label="Credits Used" value={fmtINR(o.credits_used_value_inr)} sub="₹100 per credit" testid="kpi-cm-credits" />
        <KpiCard icon={Clock} color="text-blue-500" label="Utilization" value={`${o.utilization || 0}%`} sub={`${o.hours_used}/${o.available_hours}h`} testid="kpi-cm-util" />
        <KpiCard icon={XCircle} color="text-red-500" label="Cancellation Rate" value={`${o.cancellation_rate || 0}%`} sub={`${o.cancelled} cancelled`} testid="kpi-cm-cancel" />
        <KpiCard icon={Star} color="text-yellow-500" label="Avg Rating" value={o.feedback_avg ?? '—'} sub={`${o.feedback_count || 0} reviews`} testid="kpi-cm-rating" />
        <KpiCard icon={Users} color="text-purple-500" label="New Clients" value={fmtNum(o.new_clients)} sub={`${o.returning_clients} returning`} testid="kpi-cm-newclients" />
        <KpiCard icon={Building2} color="text-cyan-500" label="VO Subscriptions" value={fmtNum(o.vo_active_subscriptions)} testid="kpi-cm-vo" />
        <KpiCard icon={TicketIcon} color="text-orange-500" label="Open Tickets" value={fmtNum(o.open_tickets)} testid="kpi-cm-tickets" />
        <KpiCard icon={Hourglass} color="text-indigo-500" label="Pending Approvals" value={fmtNum(o.pending_approvals)} testid="kpi-cm-approvals" />
        <KpiCard icon={FileText} color="text-pink-500" label="Lock-in due 30d" value={fmtNum(data.contracts_lockin_due?.['30'])} sub={`60d: ${data.contracts_lockin_due?.['60']} · 90d: ${data.contracts_lockin_due?.['90']}`} testid="kpi-cm-lockin" />
        <KpiCard icon={AlertTriangle} color="text-red-400" label="No-Shows" value={fmtNum(o.no_show)} testid="kpi-cm-noshow" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChartIcon className="w-4 h-4" />Revenue by Payment Method</CardTitle></CardHeader>
          <CardContent><RevenueByMethodPie data={data.revenue_by_method} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Daily Revenue Trend</CardTitle></CardHeader>
          <CardContent><DailyTrendChart data={data.daily_trend} lines={['revenue', 'bookings']} /></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" />Top Rooms by Revenue</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Room</th><th className="px-2">Type</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Hours</th><th className="px-2 text-right">Revenue</th></tr></thead>
            <tbody>
              {data.top_rooms?.map((r) => (
                <tr key={r.bookable_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.name}</td>
                  <td className="px-2 text-muted-foreground">{r.type}</td>
                  <td className="px-2 text-right">{r.bookings}</td>
                  <td className="px-2 text-right">{r.hours}h</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-purple-500" />Top Clients</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Client</th><th className="px-2">Email</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Credits</th><th className="px-2 text-right">Revenue</th></tr></thead>
            <tbody>
              {data.top_clients?.map((c) => (
                <tr key={c.user_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{c.name}</td>
                  <td className="px-2 text-muted-foreground text-xs">{c.email}</td>
                  <td className="px-2 text-right">{c.bookings}</td>
                  <td className="px-2 text-right text-amber-600">{c.credits}</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}

/* ============================================================
 * ADMIN SECTIONS
 * ============================================================ */
function AdminSections({ data }) {
  const o = data.overview || {};
  const mom = o.mom_growth_pct;
  const momPositive = (mom ?? 0) >= 0;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard icon={IndianRupee} color="text-emerald-500" label="Revenue" value={fmtINR(o.total_revenue)} sub={`GST ${fmtINR(o.gst_collected)}`} testid="kpi-admin-revenue" />
        <KpiCard icon={momPositive ? ArrowUpRight : ArrowDownRight} color={momPositive ? 'text-emerald-500' : 'text-red-500'} label="MoM Growth" value={mom != null ? `${mom}%` : '—'} sub={`Prev: ${fmtINR(o.previous_period_revenue)}`} testid="kpi-admin-mom" />
        <KpiCard icon={Calendar} color="text-primary" label="Total Bookings" value={fmtNum(o.total_bookings)} testid="kpi-admin-bookings" />
        <KpiCard icon={Coins} color="text-amber-500" label="Credits Used" value={fmtINR(o.credits_value_inr)} testid="kpi-admin-credits" />
        <KpiCard icon={XCircle} color="text-red-500" label="Cancellation Rate" value={`${o.cancellation_rate}%`} testid="kpi-admin-cancel" />
        <KpiCard icon={Users} color="text-purple-500" label="Total Clients" value={fmtNum(o.total_clients)} sub={`${o.new_clients} new in range`} testid="kpi-admin-clients" />
        <KpiCard icon={Building2} color="text-cyan-500" label="Active Venues" value={fmtNum(o.total_venues)} testid="kpi-admin-venues" />
        <KpiCard icon={FileText} color="text-pink-500" label="VO Active Subs" value={fmtNum(o.vo_active_subscriptions)} testid="kpi-admin-vo" />
        <KpiCard icon={Receipt} color="text-orange-500" label="Zoho Invoices Generated" value={fmtNum(data.invoice_stats?.with_zoho_invoice)} testid="kpi-admin-inv-gen" />
        <KpiCard icon={CheckCircle2} color="text-emerald-500" label="Marked Paid" value={fmtNum(data.invoice_stats?.marked_paid)} testid="kpi-admin-inv-paid" />
        <KpiCard icon={FileWarning} color="text-red-500" label="Balance Due" value={fmtNum(data.invoice_stats?.balance_due)} testid="kpi-admin-inv-due" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChartIcon className="w-4 h-4" />Revenue by Payment Method</CardTitle></CardHeader>
          <CardContent><RevenueByMethodPie data={data.revenue_by_method} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Daily Revenue Trend</CardTitle></CardHeader>
          <CardContent><DailyTrendChart data={data.daily_trend} lines={['revenue', 'bookings']} /></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-cyan-500" />Venue Performance</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Venue</th><th className="px-2">Region</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Hours</th><th className="px-2 text-right">Credits</th><th className="px-2 text-right">Revenue</th><th className="px-2 text-right">Utilization</th></tr></thead>
            <tbody>
              {data.venue_performance?.map((v) => (
                <tr key={v.venue_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{v.venue_name}</td>
                  <td className="px-2 text-muted-foreground">{v.region}</td>
                  <td className="px-2 text-right">{v.bookings}</td>
                  <td className="px-2 text-right">{v.hours}h</td>
                  <td className="px-2 text-right text-amber-600">{v.credits}</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(v.revenue)}</td>
                  <td className="px-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(v.utilization, 100)}%` }} /></div>
                      <span>{v.utilization}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" />Revenue by Region</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.revenue_by_region}>
                <CartesianGrid strokeDasharray="3 3" stroke="#88888833" />
                <XAxis dataKey="region" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="revenue" fill="#c4663a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" />Top Organizations by Spend</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Org</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Revenue</th></tr></thead>
              <tbody>
                {data.top_orgs?.map((r) => (
                  <tr key={r.org_id} className="border-b border-border/40">
                    <td className="py-2 px-2 font-medium">{r.name}</td>
                    <td className="px-2 text-right">{r.bookings}</td>
                    <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-purple-500" />Top Lifetime Customers (LTV)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Customer</th><th className="px-2">Email</th><th className="px-2 text-right">Total Bookings</th><th className="px-2 text-right">Lifetime Revenue</th></tr></thead>
            <tbody>
              {data.top_lifetime_clients?.map((r) => (
                <tr key={r.user_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.name}</td>
                  <td className="px-2 text-muted-foreground text-xs">{r.email}</td>
                  <td className="px-2 text-right">{r.bookings}</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(r.lifetime_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-pink-500" />Contracts Pipeline</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { k: 'lockin_30', l: 'Lock-in expires ≤30d', color: 'text-red-500' },
              { k: 'lockin_60', l: 'Lock-in expires ≤60d', color: 'text-orange-500' },
              { k: 'lockin_90', l: 'Lock-in expires ≤90d', color: 'text-yellow-500' },
              { k: 'license_30', l: 'License ends ≤30d', color: 'text-red-500' },
              { k: 'license_60', l: 'License ends ≤60d', color: 'text-orange-500' },
              { k: 'license_90', l: 'License ends ≤90d', color: 'text-yellow-500' },
            ].map((b) => (
              <div key={b.k} className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{b.l}</p>
                <p className={`text-xl font-bold mt-1 ${b.color}`}>{fmtNum(data.contracts_pipeline?.[b.k])}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/* ============================================================
 * FINANCE SECTIONS
 * ============================================================ */
function FinanceSections({ data }) {
  const o = data.overview || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard icon={IndianRupee} color="text-emerald-500" label="Collected" value={fmtINR(o.total_collected)} testid="kpi-fin-collected" />
        <KpiCard icon={FileWarning} color="text-red-500" label="Outstanding" value={fmtINR(o.total_outstanding)} testid="kpi-fin-outstanding" />
        <KpiCard icon={Receipt} color="text-amber-500" label="GST Collected" value={fmtINR(o.gst_collected)} testid="kpi-fin-gst" />
        <KpiCard icon={Coins} color="text-blue-500" label="Credits Value" value={fmtINR(o.credits_value_inr)} testid="kpi-fin-credits" />
        <KpiCard icon={Banknote} color="text-cyan-500" label="Credit Purchases (₹)" value={fmtINR(data.credit_purchases?.inr_received)} sub={`${data.credit_purchases?.credits || 0} credits`} testid="kpi-fin-credit-purchases" />
        <KpiCard icon={Percent} color="text-purple-500" label="Coupon Redemptions" value={fmtNum(o.coupon_redemptions)} sub={`Discount: ${fmtINR(o.coupon_total_discount)}`} testid="kpi-fin-coupons" />
        <KpiCard icon={ArrowDownRight} color="text-orange-500" label="Refunds (credits)" value={fmtNum(o.refund_credits_count)} sub={`Total: ${fmtNum(o.refund_credits_total)} credits`} testid="kpi-fin-refunds" />
        <KpiCard icon={Calendar} color="text-primary" label="Paid Bookings" value={fmtNum(o.total_bookings_paid)} testid="kpi-fin-bookings" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChartIcon className="w-4 h-4" />Revenue by Payment Method</CardTitle></CardHeader>
          <CardContent><RevenueByMethodPie data={data.revenue_by_method} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Daily Settlement (Revenue + GST)</CardTitle></CardHeader>
          <CardContent><DailyTrendChart data={data.daily_settlement} lines={['revenue', 'gst']} /></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileWarning className="w-4 h-4 text-red-500" />Invoice Aging</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[['0_30', '0-30 days', 'text-emerald-500'], ['31_60', '31-60 days', 'text-yellow-500'], ['61_90', '61-90 days', 'text-orange-500'], ['90_plus', '90+ days', 'text-red-500']].map(([k, l, color]) => (
              <div key={k} className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className={`text-xl font-bold mt-1 ${color}`}>{fmtINR(data.invoice_aging?.[k]?.amount)}</p>
                <p className="text-xs text-muted-foreground">{data.invoice_aging?.[k]?.count} invoices</p>
              </div>
            ))}
          </div>
          {data.outstanding_invoices?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Invoice</th><th className="px-2">Date</th><th className="px-2 text-right">Age (days)</th><th className="px-2 text-right">Amount</th></tr></thead>
                <tbody>
                  {data.outstanding_invoices.slice(0, 50).map((r) => (
                    <tr key={r.booking_id} className="border-b border-border/40">
                      <td className="py-2 px-2 font-medium">{r.invoice_number}</td>
                      <td className="px-2 text-muted-foreground text-xs">{r.booking_date}</td>
                      <td className="px-2 text-right">{r.days_old}</td>
                      <td className="px-2 text-right text-red-600 font-semibold">{fmtINR(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Venue Financial Snapshot</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Venue</th><th className="px-2">Region</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Credits Used</th><th className="px-2 text-right">GST</th><th className="px-2 text-right">Revenue</th></tr></thead>
            <tbody>
              {data.venue_financials?.map((r) => (
                <tr key={r.venue_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.venue_name}</td>
                  <td className="px-2 text-muted-foreground">{r.region}</td>
                  <td className="px-2 text-right">{r.bookings}</td>
                  <td className="px-2 text-right text-amber-600">{r.credits_used}</td>
                  <td className="px-2 text-right">{fmtINR(r.gst)}</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data.coupons_used?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Percent className="w-4 h-4 text-purple-500" />Coupon Redemption</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Coupon</th><th className="px-2 text-right">Uses</th><th className="px-2 text-right">Total Discount</th></tr></thead>
              <tbody>
                {data.coupons_used.map((c) => (
                  <tr key={c.code} className="border-b border-border/40">
                    <td className="py-2 px-2 font-medium"><Badge variant="outline">{c.code}</Badge></td>
                    <td className="px-2 text-right">{c.uses}</td>
                    <td className="px-2 text-right text-red-600">{fmtINR(c.total_discount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ============================================================
 * AUDITS SECTIONS
 * ============================================================ */
function AuditsSections({ data }) {
  const o = data.overview || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard icon={Calendar} color="text-primary" label="Bookings Created" value={fmtNum(o.bookings_created)} testid="kpi-aud-bookings" />
        <KpiCard icon={Coins} color="text-amber-500" label="Credit Tx" value={fmtNum(o.credit_transactions)} testid="kpi-aud-credit-tx" />
        <KpiCard icon={ArrowDownRight} color="text-orange-500" label="Refunds" value={fmtNum(o.refunds)} testid="kpi-aud-refunds" />
        <KpiCard icon={ScrollText} color="text-purple-500" label="Manual Adjustments" value={fmtNum(o.manual_adjustments)} testid="kpi-aud-adjustments" />
        <KpiCard icon={Percent} color="text-pink-500" label="Heavy Discounts (>50%)" value={fmtNum(o.heavy_discounts_count)} testid="kpi-aud-heavy" />
        <KpiCard icon={CheckCircle2} color="text-emerald-500" label="Internal Bookings" value={fmtNum(o.internal_bookings_count)} testid="kpi-aud-internal" />
        <KpiCard icon={XCircle} color="text-red-500" label="Cancellations" value={fmtNum(o.cancellations)} testid="kpi-aud-cancel" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />User Activity (bookings created by role)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">User</th><th className="px-2">Role</th><th className="px-2 text-right">Bookings Created</th></tr></thead>
            <tbody>
              {data.user_activity?.map((r) => (
                <tr key={r.user_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.name}</td>
                  <td className="px-2"><Badge variant="outline">{r.role}</Badge></td>
                  <td className="px-2 text-right">{r.bookings_created}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Percent className="w-4 h-4 text-pink-500" />Heavy Discount Bookings (Anomaly)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Booking</th><th className="px-2">Date</th><th className="px-2 text-right">Base</th><th className="px-2 text-right">Discount %</th><th className="px-2 text-right">Discount ₹</th><th className="px-2 text-right">Total</th></tr></thead>
            <tbody>
              {data.heavy_discounts?.map((r) => (
                <tr key={r.id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-mono text-xs">#{r.id?.slice(0, 8)}</td>
                  <td className="px-2 text-muted-foreground text-xs">{r.booking_date}</td>
                  <td className="px-2 text-right">{fmtINR(r.base_amount)}</td>
                  <td className="px-2 text-right text-red-600">{r.discount_percent}%</td>
                  <td className="px-2 text-right text-red-600">{fmtINR(r.discount_amount)}</td>
                  <td className="px-2 text-right">{fmtINR(r.total_amount)}</td>
                </tr>
              ))}
              {!data.heavy_discounts?.length && <tr><td colSpan="6" className="py-6 text-center text-muted-foreground text-xs">No heavy-discount anomalies in this period</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Internal Bookings by CM (Anomaly Watch)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">CM</th><th className="px-2">Role</th><th className="px-2 text-right">Internal Bookings</th><th className="px-2 text-right">Distinct Beneficiaries</th></tr></thead>
            <tbody>
              {data.internal_bookings_by_cm?.map((r) => (
                <tr key={r.cm_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.cm_name}</td>
                  <td className="px-2"><Badge variant="outline">{r.cm_role}</Badge></td>
                  <td className="px-2 text-right">{r.internal_bookings}</td>
                  <td className="px-2 text-right">{r.distinct_beneficiaries}</td>
                </tr>
              ))}
              {!data.internal_bookings_by_cm?.length && <tr><td colSpan="4" className="py-6 text-center text-muted-foreground text-xs">No internal bookings in this period</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ScrollText className="w-4 h-4 text-purple-500" />Manual Credit Adjustments</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Date</th><th className="px-2">Description</th><th className="px-2 text-right">Amount</th></tr></thead>
            <tbody>
              {data.manual_adjustments?.map((r, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-2 px-2 text-xs text-muted-foreground">{(r.created_at || '').slice(0, 10)}</td>
                  <td className="px-2 text-xs">{r.description || '—'}</td>
                  <td className={`px-2 text-right font-semibold ${(r.amount || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{r.amount}</td>
                </tr>
              ))}
              {!data.manual_adjustments?.length && <tr><td colSpan="3" className="py-6 text-center text-muted-foreground text-xs">No manual adjustments in this period</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}

/* ============================================================
 * SALES SECTIONS
 * ============================================================ */
function SalesSections({ data }) {
  const o = data.overview || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard icon={CheckCircle2} color="text-emerald-500" label="Confirmed" value={fmtNum(o.confirmed_bookings)} testid="kpi-sales-confirmed" />
        <KpiCard icon={Hourglass} color="text-orange-500" label="Pending Payment" value={fmtNum(o.pending_payment)} testid="kpi-sales-pending" />
        <KpiCard icon={XCircle} color="text-red-500" label="Cancelled" value={fmtNum(o.cancelled)} testid="kpi-sales-cancelled" />
        <KpiCard icon={Percent} color="text-purple-500" label="Conversion %" value={`${o.conversion_pct}%`} sub={`${o.total_attempted} attempted`} testid="kpi-sales-conversion" />
        <KpiCard icon={IndianRupee} color="text-emerald-500" label="Revenue" value={fmtINR(o.revenue)} sub={`GST ${fmtINR(o.gst_collected)}`} testid="kpi-sales-revenue" />
        <KpiCard icon={Coins} color="text-amber-500" label="Credits Value" value={fmtINR(o.credits_value_inr)} testid="kpi-sales-credits" />
        <KpiCard icon={FileWarning} color="text-red-500" label="Open Payment Links" value={fmtNum(o.open_payment_links)} testid="kpi-sales-links" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChartIcon className="w-4 h-4" />Revenue by Payment Method</CardTitle></CardHeader>
          <CardContent><RevenueByMethodPie data={data.revenue_by_method} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Daily Trend</CardTitle></CardHeader>
          <CardContent><DailyTrendChart data={data.daily_trend} lines={['revenue', 'bookings', 'discount']} /></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" />Sales Leaderboard</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Sales Person</th><th className="px-2">Email</th><th className="px-2 text-right">Bookings</th><th className="px-2 text-right">Discount Given</th><th className="px-2 text-right">Revenue</th></tr></thead>
            <tbody>
              {data.leaderboard?.map((r) => (
                <tr key={r.sales_user_id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-medium">{r.name}</td>
                  <td className="px-2 text-muted-foreground text-xs">{r.email}</td>
                  <td className="px-2 text-right">{r.bookings}</td>
                  <td className="px-2 text-right text-red-600">{fmtINR(r.discount_given)}</td>
                  <td className="px-2 text-right text-emerald-600 font-semibold">{fmtINR(r.revenue)}</td>
                </tr>
              ))}
              {!data.leaderboard?.length && <tr><td colSpan="5" className="py-6 text-center text-muted-foreground text-xs">No sales bookings in this period</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Ecosystem Inquiries</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(data.ecosystem_inquiries || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(data.ecosystem_inquiries).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="capitalize">{k.replace('_', ' ')}</span>
                    <Badge>{v}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No inquiries</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Contracts</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(data.contracts_pipeline || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(data.contracts_pipeline).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="capitalize">{k.replace('_', ' ')}</span>
                    <Badge>{v}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No contracts</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Virtual Office Pipeline</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(data.vo_pipeline || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(data.vo_pipeline).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="capitalize">{k.replace('_', ' ')}</span>
                    <Badge>{v}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No VO subs</p>}
          </CardContent>
        </Card>
      </div>

      {data.open_payment_links?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileWarning className="w-4 h-4 text-red-500" />Open Payment Links (Need Follow-up)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 px-2">Booking</th><th className="px-2">Date</th><th className="px-2 text-right">Amount</th><th className="px-2">Payment Link</th></tr></thead>
              <tbody>
                {data.open_payment_links.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-b border-border/40">
                    <td className="py-2 px-2 font-mono text-xs">#{r.id?.slice(0, 8)}</td>
                    <td className="px-2 text-muted-foreground text-xs">{r.booking_date}</td>
                    <td className="px-2 text-right font-semibold">{fmtINR(r.total_amount)}</td>
                    <td className="px-2 text-xs">
                      {r.razorpay_payment_link ? <a href={r.razorpay_payment_link} target="_blank" rel="noreferrer" className="text-primary underline">Open</a> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
}
